import { DateTime } from "luxon";
import { clientMutationOptions } from "./controller/clientMutation";

import { PagedResult } from "../DTOs/Common/PagedResult"
import { DiggingLocationDTO } from "../DTOs/Production/DiggingLocationDTO"
import { PitOptionDTO } from "../DTOs/Production/PitOptionDTO"
import { TruckLoadDTO } from "../DTOs/Production/TruckLoadDTO"
import { PlantBucketOptionDTO } from "../DTOs/Production/PlantBucketOptionDTO"
import { ajaxController, formatDatesForTimeOnlyModel } from "./controller/AjaxController"
import { GetDiggingLocationByDiggingPatternIdDTO } from "../DTOs/Production/GetDiggingLocationByDiggingPatternIdDTO"
import BlastMasterIdsLookupDTO from "../DTOs/Production/BlastMasterIdsLookupDTO"
import { DataTableFilteredRequest } from "../components/DataTable/TableHook";
import { TripleShift } from "../types/Shift";
import { GetDiggingSummaryPagedResult } from "../DTOs/Production/GetDiggingSummaryPagedResultDTO"
import { TruckLoadsPagedResult } from "../DTOs/Production/TruckLoadsPagedResult"
import { PagedRequest } from "../DTOs/Common/PagedRequest"
import { TruckLoadMutationResultDTO } from "../DTOs/Production/TruckLoadMutationResultDTO"

export interface TruckLoadsShiftGroup {
  diggingPatternId: number
  hour: number
  result: TruckLoadsPagedResult
}

export interface DiggerLocationsShiftGroup {
  diggerId: number
  result: PagedResult<DiggingLocationDTO>
}

class DiggingAjax {
  private _ajaxController = ajaxController
  private _diggingDataEndpoint = "/Digging"

  // Production Locations
  public getLocationByDiggingPatternId(diggingPatternId: number) {
    return this._ajaxController.get<GetDiggingLocationByDiggingPatternIdDTO>(
      `${this._diggingDataEndpoint}/locations/${diggingPatternId}`,
      {}
    )
  }

  public getDiggerLocations(
    shiftDate: DateTime,
    dayOrNight: TripleShift,
    diggerId: number
  ) {
    return this._ajaxController.get<PagedResult<DiggingLocationDTO>>(
      `${this._diggingDataEndpoint}/locations/${shiftDate.toISODate()}/${dayOrNight[0]}/${diggerId}`,
      {}
    )
  }

  public getDiggerLocationsForShift(shiftDate: DateTime, dayOrNight: TripleShift) {
    return this._ajaxController.get<DiggerLocationsShiftGroup[]>(
      `${this._diggingDataEndpoint}/locations/shift/${shiftDate.toISODate()}/${dayOrNight[0]}`,
      {}
    )
  }

  public updateDiggingLocation(dto: DiggingLocationDTO, clientId?: string) {
    return this._ajaxController.put<DiggingLocationDTO>(
      `${this._diggingDataEndpoint}/locations`, dto, formatDatesForTimeOnlyModel, clientMutationOptions(clientId)
    )
  }

  public insertDiggingLocation(
    shiftDate: DateTime,
    dayOrNight: TripleShift,
    diggerId: number,
    location: DiggingLocationDTO,
    clientId?: string
  ) {
    return this._ajaxController.post<DiggingLocationDTO, DiggingLocationDTO>(
      `${this._diggingDataEndpoint}/locations/${shiftDate.toISODate()}/${dayOrNight[0]}/${diggerId}`,
      location,
      formatDatesForTimeOnlyModel,
      false,
      clientMutationOptions(clientId)
    )
  }

  // Digging Pattern
  public deleteDiggingPattern(diggingPatternId: number, clientId?: string) {
    return this._ajaxController.delete(
      `${this._diggingDataEndpoint}/deleteDiggingPattern/${diggingPatternId}`,
      clientMutationOptions(clientId)
    )
  }

  // Truck Loads
  public getTruckLoads(diggingPatternId: number, hour: number, request?: DataTableFilteredRequest) {
    return this._ajaxController.get<TruckLoadsPagedResult>(
      `${this._diggingDataEndpoint}/truck-loads/${diggingPatternId}/${hour}`,
      request ?? {}
    );
  }

  // Note: shift-wide batch used to warm every per-pattern-per-hour truck-loads cache from one
  // request instead of one request per pattern per hour.
  public getTruckLoadsForShift(shiftDate: DateTime, dayOrNight: TripleShift, request?: PagedRequest) {
    return this._ajaxController.get<TruckLoadsShiftGroup[]>(
      `${this._diggingDataEndpoint}/truck-loads/shift/${shiftDate.toISODate()}/${dayOrNight[0]}`,
      request ?? {}
    );
  }

  public updateTruckLoad(dto: TruckLoadDTO, clientId?: string) {
    return this._ajaxController.post<TruckLoadDTO, TruckLoadMutationResultDTO>(
      `${this._diggingDataEndpoint}/truck-loads/update`,
      dto,
      undefined,
      false,
      clientMutationOptions(clientId)
    )
  }

  // Note: scoped to the pattern and hour on screen, so a user can only clear rows they are looking at.
  public verifyTruckLoads(diggingPatternId: number, hour: number) {
    return this._ajaxController.post<undefined, number>(
      `${this._diggingDataEndpoint}/truck-loads/verify/${diggingPatternId}/${hour}`,
      undefined
    );
  }

  public insertTruckLoad(dto: TruckLoadDTO, clientId?: string) {
    return this._ajaxController.post<TruckLoadDTO, TruckLoadMutationResultDTO>(
      `${this._diggingDataEndpoint}/truck-loads/insert`,
      dto,
      undefined,
      false,
      clientMutationOptions(clientId)
    )
  }

  public deleteTruckLoad(loadingId: number, clientId?: string) {
    return this._ajaxController.post<null, TruckLoadMutationResultDTO>(
      `${this._diggingDataEndpoint}/truck-loads/delete/${loadingId}`,
      null,
      undefined,
      false,
      clientMutationOptions(clientId)
    )
  }

  public copyPreviousHourLoads(diggingPatternId: number, hour: number) {
    return this._ajaxController.post<null, TruckLoadDTO[]>(
      `${this._diggingDataEndpoint}/truck-loads/copy/previous-hour/${diggingPatternId}/${hour}`,
      null
    )
  }

  public moveLoadingRecord(loadingId: number, newDiggingPatternId: number) {
    const data = { loadingId, newDiggingPatternId };

    return this._ajaxController.post<{ loadingId: number; newDiggingPatternId: number }, TruckLoadDTO[]>(
      `${this._diggingDataEndpoint}/truck-loads/move`,
      data
    )
  }

  // Options
  public getLocationOptions(shiftDate: DateTime, dayOrNight: TripleShift) {
    return this._ajaxController.get<PagedResult<PitOptionDTO>>(
      `${this._diggingDataEndpoint}/location-options/${shiftDate.toISODate()}/${dayOrNight[0]}`, {}
    )
  }

  public getPlantBuckets(
    shiftDate: DateTime,
    dayOrNight: TripleShift,
    diggerId: number,
    patternBlastId: number,
    truckId: number
  ) {
    return this._ajaxController.get<PagedResult<PlantBucketOptionDTO>>(
      `${this._diggingDataEndpoint}/truck-buckets/${shiftDate.toISODate()}/${dayOrNight[0]}/${diggerId}/${patternBlastId}/${truckId}`,
      {}
    );
  }

  public getBlastmasterIds(shiftDate: DateTime, dayOrNight: TripleShift) {
    return this._ajaxController.get<PagedResult<BlastMasterIdsLookupDTO>>(
      `${this._diggingDataEndpoint}/blastmaster-options/${shiftDate.toISODate()}/${dayOrNight[0]}`,
      {}
    )
  }

  // Digging Loading Summary
  async getDiggingLoadingSummary(
    shiftDate: DateTime,
    dayOrNight: TripleShift,
    filters: DataTableFilteredRequest
  ) {
    return this._ajaxController.get<GetDiggingSummaryPagedResult>(
      `${this._diggingDataEndpoint}/summary/${shiftDate.toISODate()}/${dayOrNight[0]}`,
      filters
    );
  }
}

export const diggingAjax = new DiggingAjax()
